import { Building2Icon, ShieldAlertIcon } from "lucide-react"
import type { CSSProperties } from "react"
import { Button } from "@/common/elements/button"

type BootstrapScreenVariant = "loading" | "unavailable" | "unauthorized" | "no-organizations"

type BootstrapScreenProps = {
  variant: BootstrapScreenVariant
  onLogout?: () => void | Promise<void>
  loadingHeading?: string
}

const variantCopy: Record<BootstrapScreenVariant, { heading: string; message: string }> = {
  loading: {
    heading: "Launching CloudFit Portal...",
    message: "",
  },
  unavailable: {
    heading: "CloudFit Portal is not available.",
    message: "Please check back later.",
  },
  unauthorized: {
    heading: "You do not have access to CloudFit Portal.",
    message: "Please contact your administrator to gain access.",
  },
  "no-organizations": {
    heading: "Your account is not associated to any organizations.",
    message: "Please contact your administrator to gain access.",
  },
}

const rippleCount = 6
const rippleDurationSeconds = 8
const rippleStaggerSeconds = rippleDurationSeconds / rippleCount
const rippleAnimationName = "cloudfit-water-ripple"
const rippleKeyframes = `
@keyframes cloudfit-water-ripple {
  0% {
    transform: translate(-50%, -50%) scale(0.6);
    opacity: 0.2;
  }

  100% {
    transform: translate(-50%, -50%) scale(4);
    opacity: 0;
  }
}
`

function VariantIcon({ variant }: { variant: BootstrapScreenVariant }) {
  if (variant === "unauthorized") {
    return <ShieldAlertIcon className="size-7 text-amber-600" />
  }

  if (variant === "no-organizations") {
    return <Building2Icon className="size-7 text-sky-600" />
  }

  return <ShieldAlertIcon className="size-7 text-rose-600" />
}

export function BootstrapScreen({ variant, onLogout, loadingHeading }: BootstrapScreenProps) {
  const copy = variantCopy[variant]
  const isLoading = variant === "loading"
  const heading = isLoading && loadingHeading ? loadingHeading : copy.heading
  const shouldShowLogout = !isLoading && typeof onLogout === "function"
  const staticOrbitClassName = "absolute inset-2 rounded-full border-2 border-slate-300"
  const rippleRingStyles: CSSProperties[] = Array.from({ length: rippleCount }, (_, index) => ({
    animationName: rippleAnimationName,
    animationDuration: `${rippleDurationSeconds}s`,
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
    animationDelay: `-${index * rippleStaggerSeconds}s`,
    opacity: 0,
    willChange: "transform, opacity",
  }))

  return (
    <div className="relative isolate flex min-h-svh items-center justify-center overflow-hidden bg-white px-6 py-10 text-slate-900">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(186,230,253,0.35),transparent_55%),radial-gradient(circle_at_80%_15%,rgba(224,242,254,0.4),transparent_45%)]" />

      <section className="relative w-full max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl sm:p-10">
        <div className="relative mx-auto mt-7 grid size-[4.5rem] place-items-center sm:size-20">
          {isLoading ? (
            <>
              <style>{rippleKeyframes}</style>
              {rippleRingStyles.map((style, index) => (
                <span
                  key={`loading-ripple-${index}`}
                  className="pointer-events-none absolute left-1/2 top-1/2 h-9 w-9 rounded-full border border-[1px] border-sky-600/80"
                  style={style}
                />
              ))}
            </>
          ) : (
            <>
              <span className="absolute inset-0 rounded-full border-2 border-sky-300/30" />
              <span className={staticOrbitClassName} />
              <VariantIcon variant={variant} />
            </>
          )}
        </div>

        <h1 className="mt-10 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{heading}</h1>
        {copy.message ? (
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-600 sm:text-base">{copy.message}</p>
        ) : null}

        {shouldShowLogout ? (
          <div className="mt-8">
            <Button
              type="button"
              variant="outline"
              className="border-slate-300 bg-white px-5 text-slate-900 hover:bg-slate-100"
              onClick={() => {
                void onLogout?.()
              }}
            >
              Sign out
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  )
}
